import { SortDirection } from '@quick/client';
import { COMMUNITIES_KEY, DETAIL, LIST, PROPERTIES_QUERY_KEY } from '../api/query';
import { useQuery, useQueryClient } from 'react-query';
import { useCurrentActiveMember, useCurrentMember } from '../redux/selector';
import { memberApi } from '../api';
import { useEffect } from 'react';

const DEFAULT_LIST_PAGE_SIZE = 10;

export const EMPTY_PROPERTY_FILTER: PropertyFilter = {
  page: 0,
  size: DEFAULT_LIST_PAGE_SIZE,
};

export interface PropertyFilter {
  page: number;
  size: number;
  address?: string;
  sortDirection?: SortDirection;
}

const propertyKeys = {
  all: (communityId: number) => [COMMUNITIES_KEY, communityId, PROPERTIES_QUERY_KEY],
  lists: (communityId: number) => [...propertyKeys.all(communityId), LIST],
  list: (communityId: number, propertyFilter: PropertyFilter = EMPTY_PROPERTY_FILTER) => [
    ...propertyKeys.lists(communityId),
    propertyFilter,
  ],
  listById: (communityId: number, properties: number[]) => [...propertyKeys.lists(communityId), ...properties],
  details: (communityId: number) => [...propertyKeys.all(communityId), DETAIL],
  detail: (communityId: number, postId: number) => [...propertyKeys.details(communityId), postId],
};

export const usePropertiesQuery = (propertyFilter: PropertyFilter) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const member = useCurrentMember()!;
  return useQuery(
    propertyKeys.list(member.communityId, propertyFilter),
    () => {
      return memberApi.listPropertyAggregatesPaginated({
        ...propertyFilter,
        communityId: member.communityId,
      });
    },
    {
      getNextPageParam: (lastPage) => (lastPage.number < lastPage.totalPages ? lastPage.number + 1 : undefined),
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      enabled: !!member,
    }
  );
};

export const usePropertiesByIdsQuery = (propertyIds: number[]) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const member = useCurrentMember()!;
  return useQuery(
    propertyKeys.listById(member.communityId, propertyIds),
    () =>
      memberApi.listPropertiesByIds({
        propertyId: propertyIds,
      }),
    {
      enabled: !!member,
    }
  );
};

export const usePrefetchProperties = () => {
  const member = useCurrentActiveMember();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!member) {
      return;
    }
    const communityId = member.communityId;
    queryClient.prefetchQuery(propertyKeys.list(communityId), () => {
      return memberApi.listPropertyAggregatesPaginated({
        communityId,
      });
    });
  }, [member, queryClient]);
};
